import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

// Store
import { Dispatch, RootState } from '../../store/store';

// Types
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';

// Constants
import ROUTE from '../../constants/route-constants';

// Components
import Table from '../../components/Table/Table';

import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import Modal from '../../components/Modal/Modal';
import IconEditOutlined from '../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../types/element-size';
import IconBin from '../../components/Icons/IconBin/IconBin';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import { DateFormatter } from '../../utils/date-formatter';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../components/Tooltip';
import capitalizeFirst from '../../utils/capitalizeFirst';

type ContractsViewProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const ContractsView: React.FC<ContractsViewProps> = ({
  contracts,
  getContracts,
  deleteContract,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();


  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<null | any>(null);

  const contractsView = useMemo(() => {
    return contracts.map((contract) => ({
      Name: contract.name,
      'Start Date': DateFormatter({ date: contract.start_at }),
      'End Date': contract.endless ? 'Endless' : DateFormatter({ date: contract.end_at }),
      Client: contract.location?.name || 'No client',
      id: contract.id,
    }))
  }, [contracts]);

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => (
        <Tooltip hint={t('commonText.edit')} >
          <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (id: string) => {
        const path = generatePath(ROUTE.EDIT_CONTRACT, { id });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: () => (
        <Tooltip hint={t('commonText.delete')} hintClassName='right-[-90%] bottom-7' isCustomSettings>
          <IconBin fill="#000" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: async (...args) => {
        openModalDeletion(args[1])
      },
      loading: false,
    },
  ];

  const fetchContracts = async () => {
    await getContracts();
    setIsDataLoaded(true);
  };

  const handleClick = () => {
    navigate(ROUTE.CREATE_CONTRACT);
  };

  const handleDeleteLocation = async (id: string) => {
    await deleteContract(id);
    await fetchContracts();
  };

  const acceptDeleteContract = () => {
    handleDeleteLocation(contractToDelete.id);
    closeModalDeletion();
  }

  const openModalDeletion = (contract) => {
    setContractToDelete(contract);
  }

  const closeModalDeletion = () => {
    setContractToDelete(null)
  }

  useEffect(() => {
    fetchContracts();
  }, []);

  return (
    <>
      <div className="p-contracts-view flex h-full w-full flex-col">
        {!isDataLoaded && (
          <div className="flex flex-auto items-center justify-center"><SpinnerLoader /></div>
        )}
        {isDataLoaded && (
          <>
            <div className="mb-5 flex w-full justify-between items-center">
              <h2 className="text-title-md2 font-semibold text-black dark:text-white">
                {capitalizeFirst(t('contract.common.contracts'))}
              </h2>
              <InteractiveButton text="Create" onClick={handleClick} />
            </div>
            <Table
              values={contractsView}
              defaultKeys={['Name', 'Start Date', 'End Date', 'Client']}
              actionsButtons={actionsButtons}
            />
            <Modal
              modalTitle={t('contract.modals.delete.title')}
              isVisible={contractToDelete !== null}
              content={t('contract.modals.delete.confirmDelete', '', { contract: contractToDelete ? contractToDelete['Name'] : t('contract.common.contract') })}
              acceptBtnText={t('commonText.delete')}
              onClose={closeModalDeletion}
              onAccept={acceptDeleteContract}
              showAcceptBtn
              showCancelBtn
            />
          </>
        )}
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  contracts: state.contracts.contracts,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getContracts: dispatch.contracts.getContracts,
  deleteContract: dispatch.contracts.deleteContract,
});

export default connect(mapState, mapDispatch)(ContractsView);
